<template>
  <div class="school-data">
    <div class="school-data__header">
      <div class="school-data__header__left">
        <h1>Fraværets varighed</h1>
        <changeSchoolYear :white-theme="true" class="marg-1-right"></changeSchoolYear>
      </div>
      <div class="school-data__header__right">
        <p class="d-link" @click="resetDates" v-if="!isDatesPristine">{{ $t('vue2.task_overview.reset') }}</p>
        <Datepickerrange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </Datepickerrange>
      </div>
    </div>
    <div class="loader-container" v-if="isLoading">
      <Loader></Loader>
      <h4>Snup en kop kaffe, det kan tage et par minutter</h4>
    </div>
    <template v-else>
      <p class="text-explainer">
        Et fraværsforløb er sammenhængende hverdage med fravær for den samme medarbejder og den samme årsag.
        Weekender afbryder ikke et forløb. Tallene viser antal forløb fordelt på, hvor mange dage de varede.
      </p>
      <div class="filters">
        <DoFiltersFilter
          title="Årsager"
          :tags="absenceReasons"
          :selectedTags="selectedReasons"
          @select="toggleReason"
          @clear="selectedReasons = []">
        </DoFiltersFilter>
      </div>
      <table class="d-table">
        <thead>
        <tr>
          <th>Årsag</th>
          <th v-for="bucket in buckets" :key="bucket.id">{{ bucket.title }}</th>
          <th>Forløb i alt</th>
          <th>Fraværsdage i alt</th>
          <th>Personer</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in rows" :key="row.name" :class="{'d-table__total': row.isTotal}">
          <td>{{ row.name }}</td>
          <td v-for="bucket in buckets" :key="bucket.id">
            {{ row.buckets[bucket.id] }}
            <span class="d-table__share" v-if="row.buckets[bucket.id]">({{ formatPercent(row.buckets[bucket.id], row.periods) }})</span>
          </td>
          <td>{{ row.periods }}</td>
          <td>{{ row.days }}</td>
          <td>{{ row.persons }}</td>
        </tr>
        <tr v-if="rows.length < 2">
          <td :colspan="buckets.length + 4" class="text-center">Intet fravær i perioden</td>
        </tr>
        </tbody>
      </table>
    </template>
  </div>
</template>

<script>
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import Loader from '../common/Loader'
import Datepickerrange from '../datepicker/DatePickerRange'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import moment from 'moment'
import schoolDataMixin from '../../mixins/schoolDataMixin'

const BUCKETS = [
  {id: '1', title: '1 dag', max: 1},
  {id: '2', title: '2 dage', max: 2},
  {id: '3-5', title: '3-5 dage', max: 5},
  {id: '6-10', title: '6-10 dage', max: 10},
  {id: '11-20', title: '11-20 dage', max: 20},
  {id: '21+', title: 'Over 20 dage', max: Infinity}
]

export default {
  components: {Datepickerrange, Loader, ChangeSchoolYear, DoFiltersFilter},
  mixins: [schoolDataMixin],
  data () {
    return {
      reportEventTypeFilter: ['absence'],
      selectedReasons: [],
      buckets: BUCKETS
    }
  },
  computed: {
    absenceReasons () {
      return this.absenceEventsInRange
        .map(event => event.title)
        .filter((title, idx, arr) => arr.indexOf(title) === idx)
    },
    periods () {
      const datesByKey = {}
      this.absenceEventsInRange.forEach(event => {
        if (this.selectedReasons.length > 0 && this.selectedReasons.indexOf(event.title) < 0) {
          return
        }
        const key = event.calendar_id + '|' + event.title
        if (!datesByKey[key]) {
          datesByKey[key] = {calendarId: event.calendar_id, reason: event.title, dates: {}}
        }
        this.getWeekdays(event).forEach(date => {
          datesByKey[key].dates[date] = true
        })
      })
      const periods = []
      Object.values(datesByKey).forEach(group => {
        let current = null
        Object.keys(group.dates).sort().forEach(date => {
          if (current && this.nextWeekday(current.lastDate) === date) {
            current.days += 1
            current.lastDate = date
          } else {
            current = {calendarId: group.calendarId, reason: group.reason, days: 1, lastDate: date}
            periods.push(current)
          }
        })
      })
      return periods
    },
    rows () {
      const rowsByName = {}
      const total = this.newRow('I alt', true)
      this.periods.forEach(period => {
        if (!rowsByName[period.reason]) {
          rowsByName[period.reason] = this.newRow(period.reason, false)
        }
        this.addPeriod(rowsByName[period.reason], period)
        this.addPeriod(total, period)
      })
      const rows = Object.values(rowsByName)
        .sort((a, b) => a.name.localeCompare(b.name, 'da'))
        .concat([total])
      rows.forEach(row => {
        row.persons = Object.keys(row.persons).length
      })
      return rows
    }
  },
  methods: {
    newRow (name, isTotal) {
      const buckets = {}
      BUCKETS.forEach(bucket => {
        buckets[bucket.id] = 0
      })
      return {name, isTotal, buckets, periods: 0, days: 0, persons: {}}
    },
    addPeriod (row, period) {
      const bucket = BUCKETS.find(bucket => period.days <= bucket.max)
      row.buckets[bucket.id] += 1
      row.periods += 1
      row.days += period.days
      row.persons[period.calendarId] = true
    },
    getWeekdays (event) {
      const dates = []
      const day = moment(event.start).startOf('day')
      const lastDay = moment(event.end).subtract(1, 'millisecond').startOf('day')
      const rangeStart = moment(this.startDate).startOf('day')
      const rangeEnd = moment(this.endDate).startOf('day')
      while (day.isSameOrBefore(lastDay)) {
        if (day.isoWeekday() < 6 && day.isSameOrAfter(rangeStart) && day.isSameOrBefore(rangeEnd)) {
          dates.push(day.format('YYYY-MM-DD'))
        }
        day.add(1, 'day')
      }
      return dates
    },
    nextWeekday (date) {
      const day = moment(date).add(1, 'day')
      while (day.isoWeekday() > 5) {
        day.add(1, 'day')
      }
      return day.format('YYYY-MM-DD')
    },
    toggleReason (reason) {
      const idx = this.selectedReasons.indexOf(reason)
      if (idx < 0) {
        this.selectedReasons.push(reason)
      } else {
        this.selectedReasons.splice(idx, 1)
      }
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.school-data {
  margin: 1rem;
  max-width: 1440px;
}

.school-data__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 1100px;
}

.school-data__header__left,
.school-data__header__right {
  display: flex;
  align-items: center;
}

.school-data__header h1 {
  margin-right: 1rem;
}

.text-explainer {
  max-width: 900px;
  color: #728195;
  margin-bottom: 1rem;
}

.filters {
  margin-bottom: 1rem;
}

.loader-container {
  text-align: center;
}

.d-link {
  color: var(--blue);
  cursor: pointer;
  margin-right: 1rem;
}

.d-link:hover {
  text-decoration: underline;
}

.d-table {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-collapse: collapse;
  background: #fff;
}

.d-table thead {
  background: #f6f9fc;
  color: #728195;
  text-transform: uppercase;
  font-size: 11px;
  border-bottom: 1px solid #e2e8f0;
}

.d-table th,
.d-table td {
  padding: 0.5rem 1rem;
  text-align: right;
}

.d-table th:first-child,
.d-table td:first-child {
  text-align: left;
}

.d-table tbody tr {
  border-bottom: 1px solid #e3e8ee;
}

.d-table tbody tr:nth-child(even) {
  background: #f9fafb;
}

.d-table tbody tr:hover {
  background: aliceblue;
}

.d-table__total {
  font-weight: bold;
}

.d-table__share {
  color: #8898aa;
  font-size: 11px;
}

.text-center {
  text-align: center !important;
  color: #8898aa;
}
</style>



// WEBPACK FOOTER //
// SchoolDataAbsenceDuration.vue?507722d2